import React, { useState, useEffect, useCallback, memo } from 'react';
import Navigation from './components/Navigation';
import FoodSection from './components/FoodCard';
import OrderSidebar from './components/OrderModal/OrderSidebar';
import MobileOrderModal from './components/OrderModal/MobileOrderModal';
import { AlertCircle } from 'lucide-react';
import Lottie from 'react-lottie';
import loadingAnimation from './assets/anim/loading.json';

const Notification = memo(({ message }) => (
  <div className="fixed z-50 transform -translate-x-1/2 bottom-20 left-1/2">
    <div className="flex items-center px-4 py-2 text-white bg-black rounded-full shadow-lg">
      <AlertCircle className="w-4 h-4 mr-2" />
      <span className="text-sm font-medium">{message}</span>
    </div>
  </div>
));

Notification.displayName = 'Notification';

const App = () => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [orderItems, setOrderItems] = useState([]);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Loading animation configuration
  const defaultOptions = React.useMemo(() => ({
    loop: true,
    autoplay: true,
    animationData: loadingAnimation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  }), []);

  const handleCategoryClick = useCallback((categoryLabel) => {
    setSelectedCategory(categoryLabel);
    
    // Use requestAnimationFrame for smooth scrolling
    requestAnimationFrame(() => {
      const sectionId = `category-${categoryLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
      const element = document.getElementById(sectionId);
      
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = window.pageYOffset + elementPosition - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    });
  }, []);

  const handleAddToOrder = useCallback((item) => {
    setOrderItems(prevItems => {
      const existingItem = prevItems.find(i => i.id === item._id);
      
      if (existingItem) {
        return prevItems.map(i => 
          i.id === item._id 
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      
      setShowNotification(true);
      
      return [...prevItems, {
        id: item._id,
        label: item.label,
        price: item.price,
        imageUrl: item.imageUrl,
        quantity: 1
      }];
    });
  }, []);

  const handleUpdateQuantity = useCallback((itemId, newQuantity) => {
    if (newQuantity < 1) return;
    
    setOrderItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId 
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  }, []);

  const handleRemoveItem = useCallback((itemId) => {
    setOrderItems(prevItems => prevItems.filter(item => item.id !== itemId));
  }, []);

  
  // Handle notification timeout
  useEffect(() => {
    let timeoutId;
    if (showNotification) {
      timeoutId = setTimeout(() => setShowNotification(false), 2000);
    }
    return () => clearTimeout(timeoutId);
  }, [showNotification]);

  // Handle initial loading
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);


  // Memoize loading component
  const LoadingComponent = memo(() => (
    <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-b from-amber-50/50 to-amber-100/30">
      <Lottie 
        options={defaultOptions}
        height={200}
        width={200}
      />
    </div>
  ));

  LoadingComponent.displayName = 'LoadingComponent';

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/50 to-amber-100/30">
      <Navigation
        selectedCategory={selectedCategory}
        onCategoryClick={handleCategoryClick}
        onAddToOrder={handleAddToOrder}  // Add this prop
      />

      <div className="flex">
        {/* Main Content */}
        <div className="w-full md:w-[calc(100%-400px)]">
          <FoodSection
            selectedCategory={selectedCategory}
            onAddToOrder={handleAddToOrder}
          />
        </div>

        {/* Desktop Order Sidebar */}
        <div className="hidden md:block fixed top-0 right-0 w-[400px] h-screen z-50">
          <OrderSidebar
            orderItems={orderItems}
            onUpdateQuantity={handleUpdateQuantity}
            onRemoveItem={handleRemoveItem}
          />
        </div>
      </div>

      {/* Mobile View Order Button */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t md:hidden">
        <button 
          onClick={() => setIsOrderModalOpen(true)}
          className="flex items-center justify-center w-full py-3 font-medium text-white transition-colors shadow-lg bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 rounded-xl"
        >
          View Order {orderItems.length > 0 && (
            <span className="flex items-center justify-center w-6 h-6 ml-2 text-sm font-bold bg-white rounded-full text-amber-700">
              {orderItems.length}
            </span>
          )}
        </button>
      </div>

      {/* Mobile Order Modal */}
      <MobileOrderModal
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        orderItems={orderItems}
        handleUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem}
      />

      {/* Added to Cart Notification */}
      {showNotification && (
        <Notification message="Item added to order" />
      )}
    </div>
  );
};

export default memo(App);
